import React from 'react';
import { University } from '../../../types';

interface TuitionTabProps {
  university: University;
}

const TuitionTab: React.FC<TuitionTabProps> = ({ university }) => {
  // Hardcoded additional costs
  const PERSONAL_EXPENSES = 2000;
  const TRANSPORTATION = 1000;

  // Calculate total cost (tuition + housing + meal plan + books + other costs)
  const calculateTotalCost = () => {
    const tuition = university.out_of_state_tuition || university.in_state_tuition || 0;
    const housing = university.average_housing_cost || 0;
    const mealPlan = university.average_meal_plan_cost || 0;
    const books = university.books_and_supplies || 0;
    const otherCosts = PERSONAL_EXPENSES + TRANSPORTATION;
    return tuition + housing + mealPlan + books + otherCosts;
  };

  // Format currency
  const formatCurrency = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return `$${num.toLocaleString()}`;
  };

  // Format percentage from decimal
  const formatPercentage = (decimal: number | null | undefined) => {
    if (!decimal) return 'N/A';
    return `${Math.round(decimal * 100)}%`;
  };

  const totalCost = calculateTotalCost();
  const netPrice = university.net_price_per_year || 0;

  return (
    <div className="tab-content">
      <h3 className="text-3xl font-bold mb-8" style={{ color: 'var(--text-primary)' }}>Tuition & Costs</h3>
      
      {/* Top Costs Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Net Price Card */}
        <div className="bg-green-50/50 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-green-500 text-2xl mr-3">
              <i className="fas fa-calculator"></i>
            </div>
            <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Net Price</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{formatCurrency(netPrice)}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>After financial aid</p>
          </div>
        </div>

        {/* Total Cost Card */}
        <div className="bg-blue-50/50 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-blue-500 text-2xl mr-3">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Total Cost</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(totalCost)}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Per academic year</p>
          </div>
        </div>
      </div>

      {/* Tuition Section */}
      <div className="border rounded-xl p-6 mb-8" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <div className="flex items-center mb-6">
          <div className="text-purple-500 text-2xl mr-3">
            <i className="fas fa-graduation-cap"></i>
          </div>
          <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Tuition</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-4 bg-purple-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>In-State</h5>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(university.in_state_tuition)}</p>
          </div>
          <div className="text-center p-4 bg-purple-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Out-of-State</h5>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(university.out_of_state_tuition)}</p>
          </div>
        </div>
      </div>

      {/* Other Fees Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Housing Card */}
        <div className="rounded-xl p-6 flex flex-col items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
          <div className="text-red-500 text-3xl mb-3">
            <i className="fas fa-bed"></i>
          </div>
          <h5 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Housing</h5>
          <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatCurrency(university.average_housing_cost)}</p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Per year</p>
        </div>

        {/* Meal Plan Card */}
        <div className="rounded-xl p-6 flex flex-col items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
          <div className="text-yellow-500 text-3xl mb-3">
            <i className="fas fa-utensils"></i>
          </div>
          <h5 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Meal Plan</h5>
          <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatCurrency(university.average_meal_plan_cost)}</p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Per year</p>
        </div>

        {/* Books & Supplies Card */}
        <div className="rounded-xl p-6 flex flex-col items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
          <div className="text-indigo-500 text-3xl mb-3">
            <i className="fas fa-book"></i>
          </div>
          <h5 className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Books & Supplies</h5>
          <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatCurrency(university.books_and_supplies)}</p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Per year</p>
        </div>
      </div>

      {/* Financial Aid Section */}
      <div className="border rounded-xl p-6 mb-8" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
        <div className="flex items-center mb-6">
          <div className="text-orange-500 text-2xl mr-3">
            <i className="fas fa-hand-holding-usd"></i>
          </div>
          <h4 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Financial Aid</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-4 bg-orange-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Average Aid Awarded</h5>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(university.average_total_aid_awarded)}</p>
          </div>
          <div className="text-center p-4 bg-orange-50/50 rounded-lg">
            <h5 className="font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Students Receiving Aid</h5>
            <p className="text-2xl font-bold text-orange-600">{formatPercentage(university.students_receiving_financial_aid)}</p>
          </div>
        </div>
      </div>

      {/* Cost Breakdown Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start">
          <div className="text-blue-500 text-xl mr-3 mt-1">
            <i className="fas fa-info-circle"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-900 mb-3">Cost Breakdown Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-800">
              <div>
                <p className="font-semibold mb-2">Direct Costs:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Tuition: {formatCurrency(university.out_of_state_tuition || university.in_state_tuition)}</li>
                  <li>• Housing: {formatCurrency(university.average_housing_cost)}</li>
                  <li>• Meal Plan: {formatCurrency(university.average_meal_plan_cost)}</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Indirect Costs:</p>
                <ul className="space-y-1 text-sm">
                  <li>• Books & Supplies: {formatCurrency(university.books_and_supplies)}</li>
                  <li>• Personal Expenses: {formatCurrency(PERSONAL_EXPENSES)}</li>
                  <li>• Transportation: {formatCurrency(TRANSPORTATION)}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TuitionTab;
